import { useCallback, useEffect, useRef, useState } from 'react';
import AttachmentService, {
  Attachment,
  AttachmentParent,
} from '@/services/AttachmentService';

const MAX_CONCURRENT = 3;

export type PendingUploadStatus =
  | 'queued'
  | 'initializing'
  | 'uploading'
  | 'completing'
  | 'failed';

export interface PendingUpload {
  localId: string;
  file: File;
  name: string;
  size: number;
  mime: string;
  status: PendingUploadStatus;
  progress: number;
  error?: string;
  attachmentId?: string;
}

interface Options {
  /** When false, list fetching is skipped (useful before a parent id exists, e.g. comment composer). */
  enabled?: boolean;
}

const makeLocalId = () =>
  `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Drives the full upload lifecycle for one attachment parent: init -> S3 ->
 * complete -> list refresh. Caps concurrency at MAX_CONCURRENT; surplus uploads
 * wait in a queue. Failed uploads stay visible with a retry handle.
 */
export default function useAttachmentUpload(
  parent: AttachmentParent | null,
  options: Options = {}
) {
  const { enabled = true } = options;
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const inFlightRef = useRef(0);
  const queueRef = useRef<PendingUpload[]>([]);
  const abortersRef = useRef<Map<string, AbortController>>(new Map());

  const parentKey = parent ? JSON.stringify(parent) : null;

  const refresh = useCallback(async () => {
    if (!parent || !enabled) return;
    setIsListLoading(true);
    setListError(null);
    try {
      const list = await AttachmentService.listAttachments(parent);
      setAttachments(list);
    } catch (err: any) {
      setListError(err?.response?.data?.message ?? err?.message ?? 'Failed to load attachments');
    } finally {
      setIsListLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentKey, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // On unmount: abort any in-flight S3 uploads. The Crafter backend will
  // GC the orphaned pending metadata server-side.
  useEffect(() => {
    const aborters = abortersRef.current;
    const queue = queueRef.current;
    return () => {
      aborters.forEach((a) => a.abort());
      aborters.clear();
      queue.length = 0;
    };
  }, []);

  const updatePending = useCallback(
    (localId: string, patch: Partial<PendingUpload>) => {
      setPending((prev) =>
        prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p))
      );
    },
    []
  );

  const removePending = useCallback((localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId));
  }, []);

  const runUpload = useCallback(
    async (item: PendingUpload) => {
      if (!parent) return;
      const { localId } = item;

      updatePending(localId, { status: 'initializing', progress: 0, error: undefined });
      try {
        const init = await AttachmentService.initUpload(parent, {
          original_name: item.name,
          mime_type: item.mime || 'application/octet-stream',
          size: item.size,
        });

        updatePending(localId, {
          status: 'uploading',
          attachmentId: init.attachment._id,
        });

        const aborter = new AbortController();
        abortersRef.current.set(localId, aborter);
        await AttachmentService.uploadToS3(
          init.upload_url,
          init.fields,
          item.file,
          (pct) => updatePending(localId, { progress: pct }),
          aborter.signal
        );
        abortersRef.current.delete(localId);

        updatePending(localId, { status: 'completing', progress: 100 });
        await AttachmentService.completeUpload(init.attachment._id);

        // Success — drop the pending row and refresh the canonical list.
        removePending(localId);
        await refresh();
      } catch (err: any) {
        // Aborted uploads (modal close / unmount) drop silently — the row is
        // already gone from React state, so no UI update is needed.
        if (err?.name === 'AbortError') {
          abortersRef.current.delete(localId);
        } else {
          const message =
            err?.response?.data?.message ?? err?.message ?? 'Upload failed';
          updatePending(localId, { status: 'failed', error: message });
        }
      } finally {
        inFlightRef.current -= 1;
        pump();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentKey, refresh, updatePending, removePending]
  );

  const pump = useCallback(() => {
    while (
      inFlightRef.current < MAX_CONCURRENT &&
      queueRef.current.length > 0
    ) {
      const next = queueRef.current.shift()!;
      inFlightRef.current += 1;
      runUpload(next);
    }
  }, [runUpload]);

  const enqueue = useCallback(
    (files: File[]) => {
      if (!parent) return;
      const rows: PendingUpload[] = files.map((file) => ({
        localId: makeLocalId(),
        file,
        name: file.name,
        size: file.size,
        mime: file.type,
        status: 'queued',
        progress: 0,
      }));
      setPending((prev) => [...prev, ...rows]);
      queueRef.current.push(...rows);
      pump();
    },
    [parent, pump]
  );

  const retry = useCallback(
    (localId: string) => {
      setPending((prev) => {
        const row = prev.find((p) => p.localId === localId);
        if (row) {
          const reset: PendingUpload = {
            ...row,
            status: 'queued',
            error: undefined,
            progress: 0,
          };
          queueRef.current.push(reset);
          pump();
          return prev.map((p) => (p.localId === localId ? reset : p));
        }
        return prev;
      });
    },
    [pump]
  );

  const remove = useCallback(
    async (attachmentId: string) => {
      // Optimistic removal; refetch on failure.
      const snapshot = attachments;
      setAttachments((prev) => prev.filter((a) => a._id !== attachmentId));
      try {
        await AttachmentService.deleteAttachment(attachmentId);
      } catch (err) {
        setAttachments(snapshot);
        throw err;
      }
    },
    [attachments]
  );

  const dismissPending = removePending;

  return {
    attachments,
    pending,
    isListLoading,
    listError,
    enqueue,
    retry,
    remove,
    refresh,
    dismissPending,
  };
}
